import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const StoryContext = createContext(null);

const STORAGE_KEY = "mintza_stories_v1";

const MOCK_STORIES = [
  {
    id: "story-1",
    user: {
      id: "user-1",
      username: "Alex",
      avatar: "https://i.pravatar.cc/150?img=12",
    },
    media: {
      type: "image",
      url: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1200",
    },
    createdAt: Date.now() - 1000 * 60 * 5,
    expiresAt: Date.now() + 1000 * 60 * 60 * 23,
    caption: "",
    privacy: "followers",
    viewed: false,
    music: null,
    mood: null,
    stickers: [],
    mentions: [],
    hashtags: [],
    interactive: null,
    effects: [],
  },
  {
    id: "story-2",
    user: {
      id: "user-2",
      username: "Jamie",
      avatar: "https://i.pravatar.cc/150?img=32",
    },
    media: {
      type: "image",
      url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200",
    },
    createdAt: Date.now() - 1000 * 60 * 20,
    expiresAt: Date.now() + 1000 * 60 * 60 * 22,
    caption: "Beach day 🌊",
    privacy: "followers",
    viewed: false,
    music: null,
    mood: null,
    stickers: [],
    mentions: [],
    hashtags: [],
    interactive: null,
    effects: [],
  },
  {
    id: "story-3",
    user: {
      id: "user-3",
      username: "Taylor",
      avatar: "https://i.pravatar.cc/150?img=47",
    },
    media: {
      type: "image",
      url: "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=1200",
    },
    createdAt: Date.now() - 1000 * 60 * 40,
    expiresAt: Date.now() + 1000 * 60 * 60 * 21,
    caption: "",
    privacy: "followers",
    viewed: true,
    music: null,
    mood: "😊",
    stickers: [],
    mentions: [],
    hashtags: [],
    interactive: null,
    effects: [],
  },
];

export function StoryProvider({ children }) {
  const [stories, setStories] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (saved) {
        return JSON.parse(saved);
      }

      return MOCK_STORIES;
    } catch {
      return MOCK_STORIES;
    }
  });

  const [currentStoryId, setCurrentStoryId] =
    useState(null);

  const [composerOpen, setComposerOpen] =
    useState(false);

  const [draft, setDraft] = useState(null);

  // Remove expired stories locally.
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();

      setStories((current) =>
        current.filter(
          (story) =>
            !story.expiresAt ||
            story.expiresAt > now
        )
      );
    };

    cleanup();

    const timer = setInterval(
      cleanup,
      60 * 1000
    );

    return () => clearInterval(timer);
  }, []);

  // Persist mock/local stories.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(stories)
      );
    } catch {
      // Storage may be unavailable.
    }
  }, [stories]);

  const activeStories = useMemo(() => {
    const now = Date.now();

    return stories.filter(
      (story) =>
        !story.expiresAt ||
        story.expiresAt > now
    );
  }, [stories]);

  const createDraft = (values = {}) => {
    setDraft({
      id: `draft-${Date.now()}`,
      media: null,
      caption: "",
      privacy: "followers",
      music: null,
      mood: null,
      stickers: [],
      mentions: [],
      hashtags: [],
      interactive: null,
      effects: [],
      ...values,
    });

    setComposerOpen(true);
  };

  const updateDraft = (updates) => {
    setDraft((current) => ({
      ...current,
      ...updates,
    }));
  };

  const publishStory = () => {
    if (!draft?.media) {
      return false;
    }

    const now = Date.now();

    const newStory = {
      ...draft,

      id: `story-${now}`,

      user: {
        id: "me",
        username: "You",
        avatar:
          "https://i.pravatar.cc/150?img=11",
      },

      createdAt: now,

      expiresAt:
        now + 24 * 60 * 60 * 1000,

      viewed: false,
    };

    setStories((current) => [
      newStory,
      ...current,
    ]);

    setDraft(null);
    setComposerOpen(false);

    return newStory;
  };

  const deleteStory = (storyId) => {
    setStories((current) =>
      current.filter(
        (story) => story.id !== storyId
      )
    );

    if (currentStoryId === storyId) {
      setCurrentStoryId(null);
    }
  };

  const markViewed = (storyId) => {
    setStories((current) =>
      current.map((story) =>
        story.id === storyId
          ? {
              ...story,
              viewed: true,
            }
          : story
      )
    );
  };

  const openStory = (storyId) => {
    setCurrentStoryId(storyId);
    markViewed(storyId);
  };

  const closeStory = () => {
    setCurrentStoryId(null);
  };

  const value = {
    stories: activeStories,

    currentStoryId,
    composerOpen,
    draft,

    setComposerOpen,
    setDraft,

    createDraft,
    updateDraft,
    publishStory,

    deleteStory,
    markViewed,

    openStory,
    closeStory,
  };

  return (
    <StoryContext.Provider value={value}>
      {children}
    </StoryContext.Provider>
  );
}

export function useStories() {
  const context = useContext(StoryContext);

  if (!context) {
    throw new Error(
      "useStories must be used inside StoryProvider"
    );
  }

  return context;
}
